import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { purchaseApi } from '../api/purchase';

interface Grn { id: number; grn_no: string; supplier_name?: string; grn_date?: string; items_total?: number; }

interface Props {
  value: string;
  onSelect: (grn: Grn) => void;
  placeholder?: string;
  status?: string;
  fontSize?: number;
}

const GrnPicker: React.FC<Props> = ({ value, onSelect, placeholder = 'Type GRN no or supplier...', status, fontSize = 12 }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Grn[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => { if (!value) setSelectedLabel(''); }, [value]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const params: any = { search: query, limit: 50 };
      if (status) params.status = status;
      purchaseApi.grns(params)
        .then(r => { setResults(r.data.data || []); setHighlight(-1); })
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query, open, status]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (inputRef.current && !inputRef.current.contains(t) && dropdownRef.current && !dropdownRef.current.contains(t)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (highlight >= 0 && itemRefs.current[highlight]) itemRefs.current[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const openDropdown = () => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + window.scrollY, left: r.left + window.scrollX, width: r.width });
    }
    setOpen(true);
    setQuery('');
  };

  const handleSelect = (g: Grn) => {
    setSelectedLabel(g.grn_no + ' - ' + (g.supplier_name || ''));
    setQuery('');
    setOpen(false);
    setHighlight(-1);
    onSelect(g);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter') openDropdown(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (highlight >= 0 && results[highlight]) handleSelect(results[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); setHighlight(-1); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        className="form-control"
        style={{ fontSize }}
        placeholder={placeholder}
        value={open ? query : selectedLabel}
        onFocus={openDropdown}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      {open && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999,
            background: '#ffffff', border: '1px solid var(--bdr)', borderRadius: 6,
            maxHeight: 260, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.25)', marginTop: 2
          }}
        >
          {results.length === 0 ? (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--tx3)' }}>{query.trim() ? 'No matches' : 'Type to search...'}</div>
          ) : results.map((g, idx) => (
            <div
              key={g.id}
              ref={el => { itemRefs.current[idx] = el; }}
              onClick={() => handleSelect(g)}
              onMouseEnter={() => setHighlight(idx)}
              style={{
                padding: '8px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid var(--bdr)',
                background: idx === highlight ? '#e8f0fe' : '#ffffff'
              }}
            >
              <div style={{ fontWeight: 600 }}>{g.grn_no} — {g.supplier_name || ''}</div>
              <div style={{ color: 'var(--tx3)', fontSize: 11 }}>
                {g.grn_date ? new Date(g.grn_date).toLocaleDateString() : ''}
                {g.items_total != null ? '  |  ' + Number(g.items_total).toFixed(2) : ''}
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default GrnPicker;
